import { useLanguage } from '@/contexts/LanguageContext';

const tagsZh = [
  { icon: 'ri-flask-line', label: '功能型材料' },
  { icon: 'ri-recycle-line', label: '循環經濟' },
  { icon: 'ri-stack-line', label: '塑膠再生' },
  { icon: 'ri-leaf-line', label: '低碳材料' },
  { icon: 'ri-earth-line', label: 'ESG 解決方案' },
];

const tagsEn = [
  { icon: 'ri-flask-line', label: 'Functional Materials' },
  { icon: 'ri-recycle-line', label: 'Circular Economy' },
  { icon: 'ri-stack-line', label: 'Plastics Reviving' },
  { icon: 'ri-leaf-line', label: 'Low-Carbon Materials' },
  { icon: 'ri-earth-line', label: 'ESG Solutions' },
];

const tagsJa = [
  { icon: 'ri-flask-line', label: '機能性材料' },
  { icon: 'ri-recycle-line', label: 'サーキュラーエコノミー' },
  { icon: 'ri-stack-line', label: 'Plastics Reviving' },
  { icon: 'ri-leaf-line', label: '低炭素材料' },
  { icon: 'ri-earth-line', label: 'ESGソリューション' },
];

export default function AboutHero() {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const isJa = lang === 'ja';
  const tags = isEn ? tagsEn : isJa ? tagsJa : tagsZh;

  const sectionLabel = isEn ? 'About Us' : isJa ? '会社概要' : '關於我們';

  const h1 = isEn ? (
    <>Material Science for a Lighter Planet,<br />
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        Greater Value for Every Brand
      </span>
    </>
  ) : isJa ? (
    <>材料科学で地球を軽くし、<br />
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        すべてのブランドに価値を
      </span>
    </>
  ) : (
    <>
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontSize: 'clamp(2.4rem, 5.5vw, 3.25rem)', display: 'inline-block', lineHeight: '1.2' }}>
        「聚量」
      </span>
      <br />
      以材料科技，為地球「減量」
    </>
  );

  const subtitle = isEn
    ? 'PolyQ Applied Materials Co., Ltd. is based in Taichung, Taiwan, specializing in functional materials and circular economy technology. Through PQernel® nano-scale polymer chain extender and its extended applications, we help the plastics industry transform waste and off-grade plastics into high-grade recycled materials that are reusable, scalable, and brand-value-creating.'
    : isJa
    ? '聚量応用材料株式会社は台湾・台中を拠点とし、機能性材料とサーキュラーエコノミー技術の開発に特化しています。PQernel®ナノスケール高分子鎖延長補強剤とその応用技術を通じて、プラスチック産業が廃棄・低品位プラスチックを再利用可能・量産可能・ブランド価値を創出できる高品位再生材料へと転換するのを支援します。'
    : '聚量應用材料股份有限公司立基於台中，專注於功能型材料與循環經濟技術開發，透過 PQernel® 奈米級高分子擴鏈補強劑與延伸應用，協助塑膠產業鏈將廢棄與次級塑料轉化為可再利用、可量產、可創造品牌價值的高階再生材料。';

  return (
    <section className="relative min-h-[560px] md:min-h-[640px] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-top" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=sustainable%20green%20technology%20company%20office%20with%20recycled%20plastic%20material%20samples%20on%20desk%20modern%20industrial%20research%20facility%20Taiwan%20circular%20economy%20clean%20professional%20environment%20green%20and%20blue%20accent%20lighting%20wide%20angle%20corporate%20photography&width=1440&height=640&seq=about-hero-v1&orientation=landscape)' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/42 via-black/28 to-black/12" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,184,148,0.28) 0%, transparent 65%)', zIndex: 2 }} aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(2,115,194,0.22) 0%, transparent 65%)', zIndex: 2 }} aria-hidden="true" />

      <div className="relative w-full max-w-screen-xl mx-auto px-4 md:px-6 py-20" style={{ zIndex: 3 }}>
        <div className="max-w-[700px] bg-black/20 backdrop-blur-[2px] rounded-2xl px-8 py-8 md:px-10 md:py-10">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-4">{sectionLabel}</p>
          <h1 className="font-bold text-white mb-6" style={{ fontSize: 'clamp(2rem, 4.5vw, 2.75rem)', lineHeight: '1.3' }}>{h1}</h1>
          <p className="text-white/85 mb-9 max-w-[620px]" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>{subtitle}</p>
          <div className="flex flex-wrap gap-2.5">
            {tags.map((tag) => (
              <div key={tag.label} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/25 backdrop-blur-sm">
                <i className={`${tag.icon} text-brand-green text-sm`}></i>
                <span className="text-white/90 text-[0.9375rem] font-medium whitespace-nowrap">{tag.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
